import { Box, Stack } from "@mui/material";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSettingsContext } from "../SettingsProvider";
import { OptionsManager } from "../components/OptionsManager";
import { SettingsPageHeader, FloatingSaveButton } from "../components";
import { RecordEditDialog } from "../components/RecordEditDialog";
import {
  sexOptionsSchema,
  genderOptionsSchema,
  pronounsOptionsSchema,
  delegateOptionsSchema,
  optionDuplicateCriteria,
  OptionRecord,
} from "../components/schemas";
import { useRecordEditor } from "../hooks";

type PatientDetailsSettings = {
  sexOptions: OptionRecord[];
  genderOptions: OptionRecord[];
  pronounsOptions: OptionRecord[];
  delegateRelationshipOptions: OptionRecord[];
};

type OptionCategory = keyof PatientDetailsSettings;

const PatientDetailsSettings = () => {
  const { settings, setSettings } = useSettingsContext();

  // Memoize original values
  const originalSettings = useMemo(() => {
    const patientDetails = (settings as any)?.patientDetails;
    return {
      sexOptions: patientDetails?.sexOptions ?? [],
      genderOptions: patientDetails?.genderOptions ?? [],
      pronounsOptions: patientDetails?.pronounsOptions ?? [],
      delegateRelationshipOptions: patientDetails?.delegateRelationshipOptions ?? [],
    } as PatientDetailsSettings;
  }, [settings]);

  // Local state - single object for all categories
  const [localSettings, setLocalSettings] = useState<PatientDetailsSettings>(originalSettings);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState<OptionCategory | null>(null);

  // Initialize local state when settings change
  useEffect(() => {
    setLocalSettings(originalSettings);
    setIsInitialized(true);
  }, [originalSettings]);

  // Calculate if dirty
  const isDirty = useMemo(() => {
    if (!isInitialized) return false;
    return JSON.stringify(localSettings) !== JSON.stringify(originalSettings);
  }, [localSettings, originalSettings, isInitialized]);

  // Create setter for a category
  const createCategorySetter = useCallback((category: OptionCategory) => {
    return (valueOrUpdater: OptionRecord[] | ((prev: OptionRecord[]) => OptionRecord[])) => {
      setLocalSettings((prev) => ({
        ...prev,
        [category]: typeof valueOrUpdater === "function"
          ? valueOrUpdater(prev[category])
          : valueOrUpdater,
      }));
    };
  }, []);

  // Record editor for each category
  const sexEditor = useRecordEditor<OptionRecord>({
    setRecords: createCategorySetter("sexOptions"),
    displayField: "code",
  });

  const genderEditor = useRecordEditor<OptionRecord>({
    setRecords: createCategorySetter("genderOptions"),
    displayField: "code",
  });

  const pronounsEditor = useRecordEditor<OptionRecord>({
    setRecords: createCategorySetter("pronounsOptions"),
    displayField: "code",
  });

  const delegateEditor = useRecordEditor<OptionRecord>({
    setRecords: createCategorySetter("delegateRelationshipOptions"),
    displayField: "code",
  });

  // Wrap edit handlers to track category
  const createEditHandler = useCallback((category: OptionCategory, editor: typeof sexEditor) => {
    return (record: OptionRecord) => {
      setEditingCategory(category);
      editor.handleEdit(record);
    };
  }, []);

  // Get current editing record
  const editingRecord = sexEditor.editingRecord || genderEditor.editingRecord || pronounsEditor.editingRecord || delegateEditor.editingRecord;

  const handleEditCancel = useCallback(() => {
    sexEditor.handleEditCancel();
    genderEditor.handleEditCancel();
    pronounsEditor.handleEditCancel();
    delegateEditor.handleEditCancel();
    setEditingCategory(null);
  }, [sexEditor, genderEditor, pronounsEditor, delegateEditor]);

  const handleEditSave = useCallback((record: OptionRecord) => {
    if (editingCategory === "sexOptions") {
      sexEditor.handleEditSave(record);
    } else if (editingCategory === "genderOptions") {
      genderEditor.handleEditSave(record);
    } else if (editingCategory === "pronounsOptions") {
      pronounsEditor.handleEditSave(record);
    } else if (editingCategory === "delegateRelationshipOptions") {
      delegateEditor.handleEditSave(record);
    }
    setEditingCategory(null);
  }, [editingCategory, sexEditor, genderEditor, pronounsEditor, delegateEditor]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await setSettings({ patientDetails: localSettings });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          <SettingsPageHeader title="Patient Details Settings" isDirty={isDirty} />

          <OptionsManager
            title="Sex Options"
            records={localSettings.sexOptions}
            onChange={createCategorySetter("sexOptions")}
            schemaDefinition={sexOptionsSchema}
            duplicateRecordCriteria={optionDuplicateCriteria}
            emptyMessage="No sex options configured. Click &quot;Add&quot; or &quot;Import CSV&quot; to get started."
            onEdit={createEditHandler("sexOptions", sexEditor)}
            onDelete={sexEditor.handleDelete}
          />

          <OptionsManager
            title="Gender Options"
            records={localSettings.genderOptions}
            onChange={createCategorySetter("genderOptions")}
            schemaDefinition={genderOptionsSchema}
            duplicateRecordCriteria={optionDuplicateCriteria}
            emptyMessage="No gender options configured. Click &quot;Add&quot; or &quot;Import CSV&quot; to get started."
            onEdit={createEditHandler("genderOptions", genderEditor)}
            onDelete={genderEditor.handleDelete}
          />

          <OptionsManager
            title="Pronoun Options"
            records={localSettings.pronounsOptions}
            onChange={createCategorySetter("pronounsOptions")}
            schemaDefinition={pronounsOptionsSchema}
            duplicateRecordCriteria={optionDuplicateCriteria}
            emptyMessage="No pronoun options configured. Click &quot;Add&quot; or &quot;Import CSV&quot; to get started."
            onEdit={createEditHandler("pronounsOptions", pronounsEditor)}
            onDelete={pronounsEditor.handleDelete}
          />

          <OptionsManager
            title="Delegate Relationship Options"
            records={localSettings.delegateRelationshipOptions}
            onChange={createCategorySetter("delegateRelationshipOptions")}
            schemaDefinition={delegateOptionsSchema}
            duplicateRecordCriteria={optionDuplicateCriteria}
            emptyMessage="No delegate relationship options configured. Click &quot;Add&quot; or &quot;Import CSV&quot; to get started."
            onEdit={createEditHandler("delegateRelationshipOptions", delegateEditor)}
            onDelete={delegateEditor.handleDelete}
          />
        </Stack>

        <RecordEditDialog
          open={editingRecord !== null}
          onClose={handleEditCancel}
          onSave={handleEditSave}
          record={editingRecord}
          schemaDefinition={
            editingCategory === "sexOptions"
              ? sexOptionsSchema
              : editingCategory === "genderOptions"
                ? genderOptionsSchema
                : editingCategory === "pronounsOptions"
                  ? pronounsOptionsSchema
                  : delegateOptionsSchema
          }
        />

        <FloatingSaveButton show={isDirty} onSave={handleSave} isSaving={isSaving} />
      </Box>
    </Box>
  );
};

export { PatientDetailsSettings };
